import { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Panel, Inp, Sel, YN, Btn, RT } from "./ui";

const CALL_TYPES_BY_INCIDENT = {
  "Medical Assist": ["Unresponsive Person", "Altered Level of Consciousness", "Difficulty Breathing", "Chest Pain",
    "Fall", "Injury", "Overdose", "Psychiatric Emergency", "Sick Person", "Unknown Medical Emergency"],
};
const LOCATION_TYPES = ["Public Building", "Library", "Residential", "Apartment", "Commercial Property", "Retail",
  "Parking Lot", "Street", "Sidewalk", "Park", "School", "Hospital", "Government Facility", "Transit Facility", "Other"];
const CASE_STATUSES = ["OPEN", "ACTIVE", "PENDING", "CLOSED", "CLEARED", "REFERRED", "SUSPENDED"];
const CRIME_AGAINST = ["Person", "Property", "Society", "Not Applicable"];

export default function SectionIncident({ rep, meta, canEdit, onChanged }) {
  const [inc, setInc] = useState(rep.incident);
  const [disp, setDisp] = useState(rep.disposition);
  const [off, setOff] = useState(rep.offense);
  const [ro, setRo] = useState(rep.reporting_officer);
  const save = async () => {
    try {
      for (const [section, data] of [["incident", inc], ["disposition", disp], ["offense", off], ["reporting_officer", ro]]) {
        await client.patch(`/rms-reports/${rep.id}/section`, { section, data });
      }
      toast.success("Incident information saved");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const I = (k) => ({ value: inc[k], onChange: (v) => setInc({ ...inc, [k]: v }), disabled: !canEdit });
  const D = (k) => ({ value: disp[k], onChange: (v) => setDisp({ ...disp, [k]: v }), disabled: !canEdit });
  const O = (k) => ({ value: off[k], onChange: (v) => setOff({ ...off, [k]: v }), disabled: !canEdit });
  const callTypeOpts = CALL_TYPES_BY_INCIDENT[inc.incident_type] || [];
  return (
    <div className="space-y-3">
      <div className="grid lg:grid-cols-2 gap-3">
        <Panel title="Incident Information" testid="panel-incident-info">
          <div className="grid grid-cols-3 gap-2">
            <Inp label="CFS / Case Number" value={rep.cad_number} onChange={() => {}} disabled testid="rr-cad" />
            <Inp label="RMS Case Number" value={rep.case_number} onChange={() => {}} disabled testid="rr-case" />
            <Sel label="Incident Type" {...I("incident_type")} options={meta.incident_types} testid="rr-incident-type" />
            {callTypeOpts.length > 0
              ? <Sel label="Call Type" {...I("call_type")} options={[...new Set([inc.call_type, ...callTypeOpts].filter(Boolean))]} testid="rr-call-type" />
              : <Inp label="Call Type" {...I("call_type")} testid="rr-call-type" />}
            <Inp label="Date Reported" {...I("date_reported")} type="date" testid="rr-date-reported" />
            <Inp label="Time Reported (24h)" {...I("time_reported")} placeholder="1800" testid="rr-time-reported" />
            <Inp label="Incident Date" {...I("incident_date")} type="date" testid="rr-incident-date" />
            <Inp label="Incident Time (24h)" {...I("incident_time")} placeholder="1800" testid="rr-incident-time" />
            <Inp label="Location Name" {...I("location_name")} placeholder="RANDALLSTOWN LIBRARY" testid="rr-location-name" />
          </div>
          <div className="grid grid-cols-4 gap-2 mt-2">
            <div className="col-span-2"><Inp label="Street Address" {...I("address")} testid="rr-address" /></div>
            <Inp label="City" {...I("city")} testid="rr-city" />
            <Inp label="State" {...I("state")} testid="rr-state" />
            <Inp label="ZIP" {...I("zip")} testid="rr-zip" />
            <Inp label="District" {...I("district")} testid="rr-district" />
            <Inp label="Beat" {...I("beat")} testid="rr-beat" />
            <Inp label="Geo Zone" {...I("geo_zone")} testid="rr-geozone" />
            <Sel label="Location Type" {...I("location_type")} options={LOCATION_TYPES} testid="rr-location-type" />
          </div>
        </Panel>
        <div className="space-y-3">
          <Panel title="Disposition" testid="panel-disposition">
            <div className="grid grid-cols-3 gap-2">
              <Sel label="Primary Disposition" {...D("primary")} options={meta.primary_dispositions} testid="rr-primary-disp" />
              <Sel label="Secondary Disposition" {...D("secondary")} options={meta.primary_dispositions} testid="rr-secondary-disp" />
              <Sel label="Case Status" {...D("case_status")} options={CASE_STATUSES} allowEmpty={false} testid="rr-case-status" />
              <YN label="Report Required" {...D("report_required")} testid="rr-report-required" />
              <YN label="Follow-Up Required" {...D("follow_up_required")} testid="rr-followup" />
              <YN label="Supervisor Notified" {...D("supervisor_notified")} testid="rr-sup-notified" />
              {disp.follow_up_required === "YES" && (<>
                <Inp label="Follow-Up Unit" {...D("follow_up_unit")} testid="rr-followup-unit" />
                <Inp label="Assigned Investigator" {...D("investigator")} testid="rr-investigator" />
                <Inp label="Due Date" {...D("due_date")} type="date" testid="rr-due-date" />
              </>)}
              <Inp label="Date/Time Cleared" {...D("cleared_datetime")} placeholder="08/19/2026 1835" testid="rr-cleared" />
              <Inp label="Cleared By Unit" {...D("cleared_by_unit")} testid="rr-cleared-by" />
            </div>
          </Panel>
          <Panel title="Offense / Incident Details" testid="panel-offense">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><Inp label="Offense" {...O("offense")} placeholder="N/A – NON-CRIMINAL / N/A – MEDICAL" testid="rr-offense" /></div>
              <Inp label="UCR/NIBRS Code" {...O("ucr_nibrs")} testid="rr-nibrs" />
              <Sel label="Attempted / Completed" {...O("attempted_completed")} options={["Attempted", "Completed"]} testid="rr-attempted" />
              <Sel label="Crime Against" {...O("crime_against")} options={CRIME_AGAINST} testid="rr-crime-against" />
              <Inp label="Weapon / Force" {...O("weapon_force")} testid="rr-weapon" />
              <YN label="Domestic Related" {...O("domestic")} testid="rr-domestic" />
              <YN label="Bias/Hate Indicator" {...O("bias")} testid="rr-bias" />
              <YN label="Gang Related" {...O("gang")} testid="rr-gang" />
              <YN label="Alcohol Related" {...O("alcohol")} unknown testid="rr-alcohol" />
              <YN label="Drug Related" {...O("drug")} unknown testid="rr-drug" />
            </div>
          </Panel>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <Panel title="Reporting Officer" testid="panel-reporting-officer">
          <div className="grid grid-cols-3 gap-2">
            <Inp label="Reporting Officer" value={`${ro.rank || ""} ${ro.name}`.trim()} onChange={() => {}} disabled testid="rr-officer" />
            <Inp label="Unit" value={ro.unit} onChange={(v) => setRo({ ...ro, unit: v })} disabled={!canEdit} testid="rr-officer-unit" />
            <Inp label="Employee ID" value={ro.employee_id} onChange={(v) => setRo({ ...ro, employee_id: v })} disabled={!canEdit} testid="rr-officer-id" />
          </div>
          {rep.review && (
            <div className="mt-2 font-mono text-[10px]" style={{ color: RT.dim }}>
              REVIEWED BY: {rep.review.rank} {rep.review.reviewed_by} · {rep.review.action} · {rep.review.reviewed_at?.slice(0, 16)}
              {rep.review.reason ? ` — ${rep.review.reason}` : ""}
            </div>
          )}
        </Panel>
        <div className="flex items-end justify-end">
          {canEdit && <Btn onClick={save} testid="rr-save-incident"><Save size={13} /> Save Incident Information</Btn>}
        </div>
      </div>
    </div>
  );
}
